import React, { useCallback, useEffect, useState, useRef } from "react";
import { subDays, parseISO } from "date-fns";
import {
    useOpeningBalancesQuery,
    useSetOpeningBalancesBulkMutation,
} from "@api/openingBalance.api";
import {
    useExtractBalanceSheet,
    useMapAccountsToCoA,
} from "@api/fileScanning.api";
import { fetchFinancialDocument } from "@lib/financialDocument";
import { BalanceSheetExtraction } from "@common/dto/financialDocument/balance-sheet-extraction.schema";
import { currencyFormatter } from "@common/helpers/currency";
import { pluralizeWithCount } from "@common/helpers/string";
import {
    BalanceSheetAccountClass,
    AccountClass,
} from "@common/constants/domains/accounting/accounts";
import {
    OpeningBalance,
    SetOpeningBalancesBulkBody,
} from "@common/schemas/domains/accounting/opening-balance.schema";
import { FinancialDocument } from "@common/types/financialDocument";
import { useAccountingTabContext } from "@components/accounting/useAccountingTab";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useAccounts } from "@hooks/useAccounts";
import {
    StandardModal,
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { usePreventBodyScroll } from "@design-system/Drawer/usePreventBodyScroll";
import { useBalanceSheetRowSync } from "./useBalanceSheetRowSync";
import { BalanceSheetImporterModalContent } from "./BalanceSheetImporterModalContent";
import {
    AccountProxyObject,
    BalanceSheetImportStep,
    BalanceSheetRowAction,
} from "./types";
import { validateBalanceSheetAccounts, ValidationResult } from "./validation";

interface Props {
    show: boolean;
    onHide: () => void;
    availableClassifications?: BalanceSheetAccountClass[];
}

const DEFAULT_NUMBER_OF_LINES = 10;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): AccountProxyObject[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        name: "",
        accountClass: null,
        balance: null,
        accountType: null,
        accountSubtype: null,
        mappedKickAccountId: null,
        action: BalanceSheetRowAction.CreateAccount,
        isActionAccepted: false,
    }));
}

function haveAccountsChanged(
    current: AccountProxyObject[],
    snapshot: AccountProxyObject[],
): boolean {
    if (current.length !== snapshot.length) {
        return true;
    }
    return current.some((currentAccount, index) => {
        const original = snapshot[index];
        return (
            currentAccount.id !== original.id ||
            currentAccount.name !== original.name ||
            currentAccount.accountClass !== original.accountClass
        );
    });
}

const isFilledAccount = ({
    name,
    mappedKickAccountId,
}: AccountProxyObject): boolean => !!name?.trim() && !!mappedKickAccountId;

const ACCOUNT_CLASS_FROM_BS: Record<string, AccountClass> = {
    [BalanceSheetAccountClass.assets]: AccountClass.assets,
    [BalanceSheetAccountClass.liabilities]: AccountClass.liabilities,
    [BalanceSheetAccountClass.equity]: AccountClass.equity,
};

export const BalanceSheetImporterModal: React.FC<Props> = React.memo(
    ({ show, onHide, availableClassifications }) => {
        const { toast } = useToaster();
        const { selectedEntity, setFilters } = useAccountingTabContext();
        const [accounts, setAccounts] =
            useState<AccountProxyObject[]>(buildEmptyLines());
        const [validationResult, setValidationResult] =
            useState<ValidationResult>({ errors: {}, display: {} });
        const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
        const [step, setStep] = useState<BalanceSheetImportStep>(
            BalanceSheetImportStep.UploadDocument,
        );
        const [extractedDate, setExtractedDate] = useState<string | null>(null);
        const mappingAccountsSnapshotRef = useRef<AccountProxyObject[] | null>(
            null,
        );

        // Disable background scrollbar while the fullscreen modal is open
        usePreventBodyScroll(show);
        // Confirmation dialog states
        const [showOverwriteConfirmation, setShowOverwriteConfirmation] =
            useState(false);
        const [existingBalances, setExistingBalances] = useState<
            OpeningBalance[]
        >([]);
        const [pendingPayload, setPendingPayload] = useState<
            Array<{ accountId: string; balance: number; description: string }>
        >([]);
        const [uploadedDocumentId, setUploadedDocumentId] = useState<
            number | null
        >(null);

        const extractBalanceSheet = useExtractBalanceSheet();
        const mapAccountsToCoA = useMapAccountsToCoA();
        const { afterChange, syncDate, initializeSnapshot } =
            useBalanceSheetRowSync({
                financialDocumentId: uploadedDocumentId,
                onSyncError: (error) =>
                    toast(`Failed to save balance sheet: ${error.message}`),
            });
        const setOpeningBalancesBulk = useSetOpeningBalancesBulkMutation();
        const { accountsById } = useAccounts({
            entityId: selectedEntity?.id,
        });
        const { data: openingBalancesByAccountId } = useOpeningBalancesQuery(
            selectedEntity?.id,
        );
        const bookkeepingStartDate = selectedEntity?.bookkeepingStartDate;

        const handleAccountsChange = useCallback(
            (newAccounts: AccountProxyObject[]) => {
                setAccounts(newAccounts);
                afterChange(newAccounts);
            },
            [afterChange],
        );

        const handleDateChange = useCallback(
            (date: string | null) => {
                setExtractedDate(date);
                syncDate(date);
            },
            [syncDate],
        );

        const validateAccounts = useCallback(
            (): ValidationResult =>
                validateBalanceSheetAccounts({
                    accounts,
                    step,
                    extractedDate,
                    bookkeepingStartDate,
                }),
            [accounts, step, extractedDate, bookkeepingStartDate],
        );

        useEffect(() => {
            setValidationResult(validateAccounts());
        }, [accounts, validateAccounts, extractedDate]);

        const submitOpeningBalances = useCallback(
            async (payload: SetOpeningBalancesBulkBody["openingBalances"]) => {
                if (!selectedEntity) {
                    return;
                }

                await setOpeningBalancesBulk.mutateAsync({
                    params: { entityId: selectedEntity.id },
                    query: {},
                    body: {
                        openingBalances: payload,
                        financialDocumentId: uploadedDocumentId ?? undefined,
                    },
                });

                toast("Opening balances created");

                // Set the balance sheet date to the day before bookkeeping start date
                if (bookkeepingStartDate) {
                    const dayBeforeStartDate = subDays(
                        parseISO(bookkeepingStartDate),
                        1,
                    );
                    setFilters((current) => ({
                        ...current,
                        startDate: dayBeforeStartDate,
                        endDate: dayBeforeStartDate,
                        datePreset: undefined,
                    }));
                }

                // Reset the form
                setAccounts(buildEmptyLines());
                setValidationResult({ errors: {}, display: {} });
                setHasAttemptedSubmit(false);
                setStep(BalanceSheetImportStep.UploadDocument);
                setPendingPayload([]);
                setUploadedDocumentId(null);
                onHide();
            },
            [
                selectedEntity,
                setOpeningBalancesBulk,
                toast,
                onHide,
                uploadedDocumentId,
                bookkeepingStartDate,
                setFilters,
            ],
        );

        const checkExistingAndSubmit = useCallback(
            async (
                payload: Array<{
                    accountId: string;
                    balance: number;
                    description: string;
                }>,
            ) => {
                if (!selectedEntity) {
                    return;
                }

                // Check for existing opening balances that differ from what's being submitted
                const payloadByAccountId = new Map(
                    payload.map((p) => [p.accountId, p.balance]),
                );
                const mismatchingBalances =
                    openingBalancesByAccountId?.filter(
                        (ob) =>
                            payloadByAccountId.has(ob.accountId) &&
                            payloadByAccountId.get(ob.accountId) !==
                                ob.openingBalance,
                    ) ?? [];

                if (mismatchingBalances.length > 0) {
                    setExistingBalances(mismatchingBalances);
                    setPendingPayload(payload);
                    setShowOverwriteConfirmation(true);
                    return;
                }

                await submitOpeningBalances(payload);
            },
            [selectedEntity, openingBalancesByAccountId, submitOpeningBalances],
        );

        const handleConfirmOverwrite = useCallback(async () => {
            setShowOverwriteConfirmation(false);
            await submitOpeningBalances(pendingPayload);
        }, [submitOpeningBalances, pendingPayload]);

        const handleSave = useCallback(async () => {
            setHasAttemptedSubmit(true);
            const result = validateAccounts();
            setValidationResult(result);

            // Check for blocking errors
            if (Object.keys(result.errors).length > 0) {
                return;
            }

            if (!bookkeepingStartDate) {
                toast("Bookkeeping start date is required");
                return;
            }

            if (!selectedEntity?.legalType) {
                toast("Entity type is required");
                return;
            }

            const filledAccounts = accounts.filter((account) =>
                isFilledAccount(account),
            );

            // Prepare opening balances payload
            const payload = filledAccounts.map((account) => ({
                accountId: account.mappedKickAccountId!,
                balance: account.balance ?? 0,
                description: `${account.name} opening balance`,
            }));

            await checkExistingAndSubmit(payload);
        }, [
            validateAccounts,
            bookkeepingStartDate,
            toast,
            selectedEntity?.legalType,
            accounts,
            checkExistingAndSubmit,
        ]);

        const handleHide = useCallback(() => {
            mappingAccountsSnapshotRef.current = null;
            setAccounts(buildEmptyLines());
            setValidationResult({ errors: {}, display: {} });
            setHasAttemptedSubmit(false);
            setStep(BalanceSheetImportStep.UploadDocument);
            setUploadedDocumentId(null);
            onHide();
        }, [onHide]);

        const handleStepChange = useCallback(
            async (newStep: BalanceSheetImportStep) => {
                if (
                    newStep === BalanceSheetImportStep.MapAccounts &&
                    step === BalanceSheetImportStep.EnterBalances
                ) {
                    setHasAttemptedSubmit(true);
                    const result = validateAccounts();
                    setValidationResult(result);

                    // Check for blocking errors
                    if (Object.keys(result.errors).length > 0) {
                        return;
                    }

                    if (!selectedEntity) {
                        toast("Select an entity first");
                        return;
                    }

                    const accountNamesWithBalances = accounts.filter(
                        (account) => account.name?.trim(),
                    );

                    if (accountNamesWithBalances.length === 0) {
                        toast("Enter at least one account name");
                        return;
                    }

                    // Filter out empty rows before moving to step 2
                    const filledAccounts = accounts.filter((account) =>
                        account.name?.trim(),
                    );

                    // Group accounts by class
                    const accountNamesByClass = {
                        [AccountClass.assets]: accountNamesWithBalances
                            .filter(
                                (account) =>
                                    account.accountClass ===
                                    AccountClass.assets,
                            )
                            .map((account) => account.name),
                        [AccountClass.liabilities]: accountNamesWithBalances
                            .filter(
                                (account) =>
                                    account.accountClass ===
                                    AccountClass.liabilities,
                            )
                            .map((account) => account.name),
                        [AccountClass.equity]: accountNamesWithBalances
                            .filter(
                                (account) =>
                                    account.accountClass ===
                                    AccountClass.equity,
                            )
                            .map((account) => account.name),
                    };

                    // Filter out empty rows and proceed to mapping step immediately
                    setAccounts(filledAccounts);
                    setStep(newStep);

                    // Start AI mapping in background
                    mappingAccountsSnapshotRef.current = filledAccounts;

                    try {
                        const mappingResult =
                            await mapAccountsToCoA.mutateAsync({
                                entityId: selectedEntity.id,
                                accountNames: {
                                    ...accountNamesByClass,
                                    [AccountClass.income]: [],
                                    [AccountClass.expenses]: [],
                                },
                            });

                        const snapshot = mappingAccountsSnapshotRef.current;
                        const shouldSkipMapping =
                            !snapshot ||
                            haveAccountsChanged(filledAccounts, snapshot);

                        if (!shouldSkipMapping) {
                            const updatedAccounts = filledAccounts.map(
                                (account) => {
                                    const mapping = mappingResult.mappings.find(
                                        (m) => m.name === account.name,
                                    );

                                    if (!mapping) {
                                        return account;
                                    }

                                    return {
                                        ...account,
                                        mappedKickAccountId:
                                            account.mappedKickAccountId ??
                                            mapping.suggested_account_id,
                                    };
                                },
                            );
                            setAccounts(updatedAccounts);

                            if (mappingResult.warnings.length > 0) {
                                toast(
                                    `Mapped accounts. ${mappingResult.warnings[0]}`,
                                );
                            }
                        }
                    } catch (error) {
                        toast(
                            `Failed to map accounts: ${error instanceof Error ? error.message : "Please try again."}`,
                        );
                    }

                    return;
                }

                if (newStep === BalanceSheetImportStep.EnterBalances) {
                    setHasAttemptedSubmit(false);
                }

                setStep(newStep);
            },
            [
                step,
                validateAccounts,
                selectedEntity,
                accounts,
                mapAccountsToCoA,
                toast,
            ],
        );

        const loadExtractionIntoState = useCallback(
            (extraction: BalanceSheetExtraction) => {
                const newAccounts: AccountProxyObject[] =
                    extraction.accounts.map((account) => ({
                        id: Math.random().toString(),
                        name: account.name,
                        accountClass: account.classification
                            ? (ACCOUNT_CLASS_FROM_BS[account.classification] ??
                              null)
                            : null,
                        balance: account.balance,
                        accountType: null,
                        accountSubtype: null,
                        mappedKickAccountId: null,
                        action: BalanceSheetRowAction.CreateAccount,
                        isActionAccepted: false,
                    }));

                setExtractedDate(extraction.date);

                const allAccounts = [...newAccounts, ...buildEmptyLines()];
                initializeSnapshot(allAccounts, extraction.date);
                setAccounts(allAccounts);
                setStep(BalanceSheetImportStep.EnterBalances);
            },
            [initializeSnapshot],
        );

        const processExtractedBalanceSheet = useCallback(
            async (documentId: number) => {
                if (!selectedEntity) {
                    toast("Select an entity first");
                    return;
                }

                setUploadedDocumentId(documentId);

                try {
                    const result = await extractBalanceSheet.mutateAsync({
                        entityId: selectedEntity.id,
                        documentId,
                    });

                    loadExtractionIntoState(result);

                    if (result.warnings.length > 0) {
                        toast(
                            `Extracted ${pluralizeWithCount("account", result.accounts.length)}. ${result.warnings[0]}`,
                        );
                    }
                } catch (uploadError) {
                    toast(
                        `Failed to upload file: ${uploadError instanceof Error ? uploadError.message : "Please try again."}`,
                    );
                }
            },
            [
                selectedEntity,
                extractBalanceSheet,
                toast,
                loadExtractionIntoState,
            ],
        );

        const handleDocumentSelect = useCallback(
            async (documentId: number) => {
                setUploadedDocumentId(documentId);

                try {
                    const document = await fetchFinancialDocument(documentId);
                    const balanceSheet = document.balanceSheetExtraction;
                    if (balanceSheet?.rows && balanceSheet.rows.length > 0) {
                        loadExtractionIntoState({
                            accounts: balanceSheet.rows.map((row) => ({
                                name: row.name,
                                balance: row.balance,
                                classification: row.classification,
                            })),
                            date: balanceSheet.date ?? null,
                            warnings: balanceSheet.warnings ?? [],
                        });
                        return;
                    }
                } catch {
                    // Fetch failed — fall through to re-extract from scratch
                }

                await processExtractedBalanceSheet(documentId);
            },
            [processExtractedBalanceSheet, loadExtractionIntoState],
        );

        const handleFileUploadCompleted = useCallback(
            async (uploadedDocuments: FinancialDocument[]) => {
                const financialDocument = uploadedDocuments[0];
                if (financialDocument) {
                    await processExtractedBalanceSheet(financialDocument.id);
                }
            },
            [processExtractedBalanceSheet],
        );

        return (
            <>
                <StandardModal
                    fullscreen
                    onHide={handleHide}
                    show={show}
                    disableAnimation
                >
                    {bookkeepingStartDate ? (
                        <FinancialDocumentUploadProvider
                            onCompleted={handleFileUploadCompleted}
                        >
                            <BalanceSheetImporterModalContent
                                loading={false}
                                availableClassifications={
                                    availableClassifications
                                }
                                accounts={accounts}
                                onAccountsChange={handleAccountsChange}
                                onSave={handleSave}
                                validationResult={validationResult}
                                hasAttemptedSubmit={hasAttemptedSubmit}
                                onDocumentSelect={handleDocumentSelect}
                                isExtracting={extractBalanceSheet.isPending}
                                isMapping={mapAccountsToCoA.isPending}
                                step={step}
                                onStepChange={handleStepChange}
                                bookkeepingStartDate={bookkeepingStartDate}
                                extractedDate={extractedDate}
                                onExtractedDateChange={handleDateChange}
                                uploadedDocumentId={uploadedDocumentId}
                            />
                        </FinancialDocumentUploadProvider>
                    ) : (
                        <>
                            <StandardModalHeader>
                                Import Balance Sheet
                            </StandardModalHeader>
                            <StandardModalBody className="p-4 text-center">
                                <p className="text-muted">
                                    Please set a bookkeeping start date for this
                                    entity before importing opening balances.
                                </p>
                            </StandardModalBody>
                        </>
                    )}
                </StandardModal>

                {/* Overwrite confirmation dialog */}
                <ConfirmationModal
                    show={showOverwriteConfirmation}
                    onHide={() => setShowOverwriteConfirmation(false)}
                    onConfirm={handleConfirmOverwrite}
                    onReject={() => setShowOverwriteConfirmation(false)}
                    title="Existing Opening Balances"
                    yes="Overwrite"
                    no="Cancel"
                    question={
                        <>
                            <p>
                                The following accounts already have opening
                                balances:
                            </p>
                            <ul className="mb-3">
                                {existingBalances.map((ob) => {
                                    const account = accountsById[ob.accountId];
                                    return (
                                        <li key={ob.accountId}>
                                            {account?.name ?? ob.accountId}:{" "}
                                            {currencyFormatter.format(
                                                ob.openingBalance,
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                            <p>
                                Importing will overwrite these values. Continue?
                            </p>
                        </>
                    }
                />
            </>
        );
    },
);
